import React, { useRef, useEffect, useState } from 'react';
import { Button } from './ui/button';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { Canvas, useFrame } from '@react-three/fiber';
import { useGLTF, OrbitControls } from '@react-three/drei';
import { Suspense } from 'react';

// 3D Prescription Bottle Component
function PrescriptionBottle(props) {
  const { scene } = useGLTF('/prescription_bottle.glb');
  const meshRef = useRef();
  const [hovered, setHovered] = useState(false);

  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.rotation.y += 0.005;
      meshRef.current.position.y = Math.sin(state.clock.elapsedTime) * 0.1;
      meshRef.current.scale.setScalar(hovered ? 2.6 : 2.4);
    }
  });

  return (
    <primitive 
      ref={meshRef}
      object={scene} 
      scale={[2.4, 2.4, 2.4]} 
      position={[0, 0, 0]} 
      onPointerOver={() => setHovered(true)}
      onPointerOut={() => setHovered(false)}
      {...props} 
    />
  );
}

export const NewHeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="relative min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 overflow-hidden">
      {/* Animated Background Layers */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-blue-600/20 via-purple-600/10 to-transparent animate-pulse"></div>
        <div className="absolute top-1/4 right-0 w-1/2 h-1/2 bg-gradient-to-l from-cyan-500/20 to-transparent blur-3xl"></div>
        <div className="absolute bottom-1/4 left-0 w-1/2 h-1/2 bg-gradient-to-r from-blue-500/20 to-transparent blur-3xl"></div>
      </div>

      {/* 3D Background Canvas */}
      <div className="absolute inset-0 opacity-30">
        <Canvas 
          camera={{ position: [0, 0, 5], fov: 75 }}
          style={{ background: 'transparent' }}
        >
          <Suspense fallback={null}>
            <ambientLight intensity={0.5} />
            <pointLight position={[10, 10, 10]} intensity={1} />
            <PrescriptionBottle />
          </Suspense>
        </Canvas>
      </div>

      {/* Floating Pills Animation */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className={`absolute w-3 h-6 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full opacity-20`}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `float ${5 + Math.random() * 5}s ease-in-out infinite ${Math.random() * 2}s`,
              transform: `rotate(${Math.random() * 360}deg)`
            }}
          />
        ))}
      </div>

      {/* Main Content */}
      <div className="relative z-20 max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-20">
        <div className="text-center">
          {/* Intro Tag */}
          <div className={`inline-flex items-center px-6 py-3 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-sm font-medium mb-8 transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <div className="w-2 h-2 bg-green-400 rounded-full mr-3 animate-pulse"></div>
            Introducing Next-Gen Drug Disposal
          </div>

          {/* Main Headlines */}
          <h1 className={`text-6xl md:text-8xl text-white mb-8 leading-tight transition-all duration-1000 delay-300 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`} style={{ fontWeight: 600 }}>
            <span className="block">Destroy</span>
            <span className="block bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent">Rx Medications</span>
            <span className="block text-5xl md:text-6xl font-medium text-gray-300">& Illicit Drugs</span>
            <span className="block text-4xl md:text-5xl font-light text-gray-400">Safely On-Site</span>
          </h1>

          {/* Subheadline */}
          <p className={`text-xl md:text-2xl text-gray-300 mb-12 max-w-5xl mx-auto leading-relaxed transition-all duration-1000 delay-500 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <span className="text-white font-semibold">NarcX+™</span> is the <span className="text-cyan-400 font-bold">ONLY</span> environmentally friendly method of on-site drug disposal that is validated and meets and exceeds the strict definition of Non-Retrievable in the Code of Federal Regulations 21 CFR 1300.05.
          </p>

          {/* Value Props Grid */}
          <div className={`grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 transition-all duration-1000 delay-700 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:bg-white/10 transition-all duration-300">
              <div className="text-cyan-400 text-3xl font-bold mb-2">Minutes</div>
              <div className="text-white font-medium mb-1">to Hours</div>
              <div className="text-gray-400 text-sm">Destruction Time</div>
            </div>
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:bg-white/10 transition-all duration-300">
              <div className="text-green-400 text-3xl font-bold mb-2">100%</div>
              <div className="text-white font-medium mb-1">Eco-Friendly</div>
              <div className="text-gray-400 text-sm">Biodegradable Solution</div>
            </div>
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:bg-white/10 transition-all duration-300">
              <div className="text-blue-400 text-3xl font-bold mb-2">CFR</div>
              <div className="text-white font-medium mb-1">Compliant</div>
              <div className="text-gray-400 text-sm">21 CFR 1300.05</div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className={`flex flex-col sm:flex-row gap-4 justify-center mb-16 transition-all duration-1000 delay-900 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <a href="#contact">
              <Button className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 hover:scale-105 hover:shadow-2xl border-0 group">
                Let's Talk
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
            <a href="https://www.narcx.com/products/" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 hover:scale-105">
                Explore Products
              </Button>
            </a>
          </div>

          {/* Scroll Indicator */}
          <div className="flex flex-col items-center text-white/60">
            <div className="text-sm font-medium mb-3">Discover the Science</div>
            <ChevronDown className="w-6 h-6 animate-bounce" />
          </div>
        </div>
      </div>

      {/* Bottom Gradient Fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent"></div>
    </section>
  );
};